import type { Shift } from './mock-data'

/**
 * The branch lens.
 *
 * Almost every collection in the store is ORG-wide while a till stands at
 * exactly ONE branch, so anything scanning them needs the same question asked
 * the same way: does this row belong to the branch I am standing at?
 *
 * Written twice on 2026-09-10 — once for shifts, once for cashiers — before
 * being pulled into `matchesActiveBranch` below. Every future branch-scoped
 * read should use it rather than re-deriving the four clauses, because the
 * clause that gets forgotten is never the same one twice.
 *
 * Pure, and outside `store.tsx` for the reason `resolveAddons` and `blendCost`
 * are: it can be pinned by tests without booting the store and Supabase with
 * it. `store.tsx` re-exports, so callers still import from the store.
 */

/** The slice of store state these read. Structural on purpose — the full
 *  `StoreState` satisfies it, and a test can hand over three fields. */
export type ShiftLens = {
  shifts: Shift[]
  branches: { id: string }[]
  activeBranchId: string | null
}

/**
 * Does a row carrying `branchId` belong to the branch the till stands at?
 *
 * Three deliberate escape hatches, all meaning "match anything":
 * - a single-branch org, where the branch is not a distinction at all;
 * - a row with no `branch_id` — a pre-041 shift, or a cashier who floats
 *   between shops on purpose (066);
 * - no active branch chosen yet, before the first hydrate settles.
 *
 * The NULL hatch points the safe way in both uses. A legacy OPEN shift matches
 * every branch, so it blocks selling until someone closes it rather than being
 * silently skipped; and a cashier with no branch keeps working everywhere,
 * which is what lets 066 ship without locking anyone out.
 */
export function matchesActiveBranch(
  branchId: string | null | undefined,
  s: Pick<ShiftLens, 'branches' | 'activeBranchId'>,
): boolean {
  return (
    s.branches.length <= 1 ||
    !branchId ||
    !s.activeBranchId ||
    branchId === s.activeBranchId
  )
}

/** The open shift of the ACTIVE branch (org-wide when single-branch). */
export function selectOpenShift(s: ShiftLens): Shift | undefined {
  return s.shifts.find((x) => x.status === 'open' && matchesActiveBranch(x.branch_id, s))
}

/**
 * 067: the shift this cashier already holds open at ANOTHER branch, if any.
 *
 * One person stands at one drawer. With two shifts open, the same name signs
 * two Z-reports and a shortage at either branch has two equally plausible
 * explanations. Found live on 2026-09-11 — „ჯაბა" open at both MINIMAX
 * branches — and the owner closed it: „2 ჯაბა არ არსებობს".
 *
 * Convenience, not the boundary: `open_shift` refuses it in SQL and a unique
 * index backs that. What this buys is the MESSAGE, naming the branch before
 * the cashier presses anything — the same reason 066's wrong-branch check
 * exists in the dialog.
 *
 * "Another branch" is `!matchesActiveBranch`, so all three of its hatches carry
 * over: a single-branch org never meets this (061 already allows one shift
 * there), and a legacy NULL-branch shift is left to the branch rule, which
 * blocks every till until someone closes it.
 */
export function openShiftElsewhere(cashierId: string, s: ShiftLens): Shift | undefined {
  return s.shifts.find(
    (x) => x.status === 'open' && x.cashier_id === cashierId && !matchesActiveBranch(x.branch_id, s),
  )
}

/**
 * The most recently closed shift AT THE ACTIVE BRANCH — the Z-report a cashier
 * standing at this till is asking for, which is never another branch's.
 *
 * The branch half is not cosmetic. Found on 2026-09-10 by standing at a second
 * branch that had never opened a shift and being offered the MAIN branch's
 * Z-report: another branch's takings, under another cashier's name, on a
 * document that prints. The POS offers this button precisely because `/staff`,
 * where the history lives, is owner-only (migration 057), so a cashier has no
 * second source to notice the mismatch against.
 */
export function selectLastClosedShift(s: ShiftLens): Shift | undefined {
  const closed = s.shifts.filter(
    (x) => x.status === 'closed' && x.closed_at && matchesActiveBranch(x.branch_id, s),
  )
  if (closed.length === 0) return undefined
  return closed.reduce((a, b) => (a.closed_at! > b.closed_at! ? a : b))
}

/**
 * A shift left open for days.
 *
 * SAMGORI2026 kept one open from 2026-09-05 for days, its cash never counted;
 * ANNASHOP's setup-day test shift was still open eight days later, the week
 * before its first customer — every sale on opening day would have landed in
 * it. Nothing in the database objects: one open shift is legal however old it
 * is, and `create_sale` must not refuse it (an offline replay may never lose
 * a sale). So the only defence is to SAY it, wherever the shift is visible.
 *
 * Stale = open 36 hours or more. The number is read off the last three weeks
 * of real shifts (2026-09-23), after a first draft („an earlier calendar day
 * and 12 hours") was checked against them and would have nagged:
 *   - SAMGORI2026 hands over every evening at 18:00–20:00; a 22–25 h shift
 *     across midnight is its ROUTINE, and the draft flagged it for ~13 hours
 *     a night.
 *   - AccessoryShop closes every day or two; 30–35 h is its habit.
 *   - The forgotten drawers were 80–120 h: SAMGORI 05.09 → 10.09 with its cash
 *     uncounted, AccessoryShop 04.09 → 09.09, ANNASHOP's test shift.
 * 36 h flags none of the routine and every one of those. `days` is calendar
 * days in local time — it only words the message.
 */
export const STALE_SHIFT_HOURS = 36

export function shiftAge(openedAt: string, now: Date): { days: number; stale: boolean } {
  const opened = new Date(openedAt)
  const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  // round, not floor: a day with a DST change is 23 or 25 hours long
  const days = Math.round((dayStart(now) - dayStart(opened)) / 86_400_000)
  const hours = (now.getTime() - opened.getTime()) / 3_600_000
  return { days, stale: hours >= STALE_SHIFT_HOURS }
}

/**
 * Every stale OPEN shift in the org, oldest first. Deliberately NOT branch
 * scoped: this is for the owner, and a drawer nobody has counted for a week is
 * his wherever it stands.
 */
export function staleOpenShifts(shifts: Shift[], now: Date): { shift: Shift; days: number }[] {
  return shifts
    .filter((s) => s.status === 'open')
    .map((shift) => ({ shift, ...shiftAge(shift.opened_at, now) }))
    .filter((x) => x.stale)
    .sort((a, b) => a.shift.opened_at.localeCompare(b.shift.opened_at))
    .map(({ shift, days }) => ({ shift, days }))
}

/** „გუშინ" / „8 დღის წინ" — completes „ცვლა … გაიხსნა". */
export function openedAgo(days: number): string {
  return days === 1 ? 'გუშინ' : `${days} დღის წინ`
}
